// @ts-nocheck
/* eslint-disable @typescript-eslint/strict-boolean-expressions */
import { Dayjs } from 'dayjs';

import { IndicatorManager } from './IndicatorManager';
import { HiroChartDatafeedIntfc } from 'types/hiro';

import { CoreEquity, PriceLineKeys, PriceLineKey } from 'types';
import { getEquitiesBySyms, sigHighLow } from 'util/hiro';
import { REVERSE_FIELD_NAME_MAPPING } from 'config/equityhub';
import { VisiblePriceRange } from 'charting_library';
import { DefaultService } from 'gen/stream-api/requests/services.gen';
import { dayjs, ET, getDateFormatted } from '../../util';
import { CANDLE_IDX } from 'types/impliedVol';

const LABEL_HEIGHT = 12;

export class KeyLevelsManager extends IndicatorManager<PriceLineKey> {
  equitiesBySymFromDateFromSym: Record<string, Record<number, CoreEquity>>;

  openingFromDateFromSym: Record<string, Record<number, PriceDate>>;

  constructor(datafeed: HiroChartDatafeedIntfc) {
    super(datafeed);
    this.equitiesBySymFromDateFromSym = {};
    this.openingFromDateFromSym = {};
  }

  async fetchAssets(dates: Dayjs[]) {
    await Promise.all([
      ...Object.keys(this.artifactsFromKeyFromSym).flatMap((sym) => {
        const equitiesBySymFromDate = (this.equitiesBySymFromDateFromSym[sym] =
          this.equitiesBySymFromDateFromSym[sym] ?? {});
        return dates
          .filter((d) => !(d.valueOf() in equitiesBySymFromDate))
          .map(
            (date: Dayjs): Promise<void> =>
              getEquitiesBySyms(date, sym).then((equitiesBySym) => {
                if (equitiesBySym) {
                  equitiesBySymFromDate[date.valueOf()] = equitiesBySym;
                }
              }),
          );
      }),
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
      ...new Set<string>([...Object.keys(this.artifactsFromKeyFromSym), 'SPX'])
        .values()
        .flatMap((symbol: string) => {
          const openingFromDate = (this.openingFromDateFromSym[symbol] =
            this.openingFromDateFromSym[symbol] ?? {});
          return dates
            .filter((d) => !(d.valueOf() in openingFromDate))
            .map((date: Dayjs): Promise<void> => {
              const marketOpen = date
                .tz(ET)
                .hour(9)
                .minute(30)
                .second(0)
                .millisecond(0);
              if (marketOpen > dayjs()) {
                return Promise.resolve();
              }

              return DefaultService.getPrices({
                symbol,
                field: 'price',
                timeGte: marketOpen.format(),
                candleDurationMs: 5_000,
                limit: 11,
              }).then((rsp) => {
                const price = rsp.candles
                  .map((c) => c[CANDLE_IDX.OPEN])
                  .find(Boolean);
                openingFromDate[date.valueOf()] = {
                  sym: symbol,
                  date: getDateFormatted(date),
                  price,
                };
              });
            });
        }),
    ]);
  }

  getPrice(
    sym: string,
    keyLevel: PriceLineKey,
    date: Dayjs,
  ): number | undefined {
    const equitiesBySym: CoreEquity | undefined =
      this.equitiesBySymFromDateFromSym[sym]?.[date.valueOf()];
    if (!equitiesBySym) {
      return;
    }
    switch (keyLevel) {
      case 'SG Implied 1d Move High':
      case 'SG Implied 1d Move Low': {
        const highLow = sigHighLow(
          equitiesBySym,
          [
            this.openingFromDateFromSym[sym]?.[date.valueOf()],
            this.openingFromDateFromSym['SPX']?.[date.valueOf()],
          ],
          getDateFormatted(date.tz(ET)),
        );
        return keyLevel === 'SG Implied 1d Move Low'
          ? highLow?.sig_low
          : highLow?.sig_high;
        break;
      }
      default:
        return equitiesBySym?.[REVERSE_FIELD_NAME_MAPPING[keyLevel]] as
          | number
          | undefined;
    }
  }

  async createArtifacts(dates: Dayjs[]) {
    await Promise.all(
      Object.entries(this.artifactsFromKeyFromSym).flatMap(
        ([sym, artifactsFromKey]): Promise<void>[] | undefined =>
          Object.entries(artifactsFromKey).flatMap(
            ([keyLevel, artifacts]): Promise<void>[] | undefined => {
              if (!artifacts.enabled) {
                return;
              }
              let labelCreated = false;
              return dates.flatMap(
                (d, dateIdx): Promise<void>[] | undefined => {
                  if (artifacts.entityIdsFromDayStartMs[d.valueOf()]?.length) {
                    return;
                  }

                  const price = this.getPrice(sym, keyLevel, d);

                  if (!price) {
                    return;
                  }
                  let beg = d.unix();
                  if (
                    this.visibleRangeBegMs &&
                    beg < this.visibleRangeBegMs / 1_000
                  ) {
                    beg = this.visibleRangeBegMs / 1_000;
                  }

                  let end = d.add(1, 'day').unix();
                  if (
                    this.visibleRangeEndMs &&
                    end > this.visibleRangeEndMs / 1_000
                  ) {
                    end = this.visibleRangeEndMs / 1_000;
                  }
                  const chart =
                    this.datafeed.chartingLibraryWidget?.activeChart();
                  if (!chart) {
                    return;
                  }

                  const paneHeight = chart
                    .getPanes()
                    .find((p) => p.hasMainSeries())
                    ?.getHeight();

                  // prettier-ignore
                  // eslint-disable-next-line
                  const priceScale: VisiblePriceRange | undefined = chart.getSeries()?.priceScale()?.getVisiblePriceRange();

                  if (!priceScale || !paneHeight) {
                    return;
                  }

                  const entityIds = (artifacts.entityIdsFromDayStartMs[
                    d.valueOf()
                  ] = artifacts.entityIdsFromDayStartMs[d.valueOf()] ?? []);

                  // if multiple key levels have similar values, space them out horizontally
                  const priceRange = priceScale.to - priceScale.from;
                  const priceFraction =
                    (priceRange * LABEL_HEIGHT) / paneHeight;

                  const numPriorLabels = PriceLineKeys.slice(
                    0,
                    PriceLineKeys.findIndex((k) => k === keyLevel),
                  ).reduce((numPriorLablesAccum, k) => {
                    const priorPrice = this.getPrice(sym, k, d);
                    return (
                      numPriorLablesAccum +
                      (Math.abs(priorPrice - price) <
                      priceFraction * (numPriorLablesAccum + 1)
                        ? 1
                        : 0)
                    );
                  }, 0);

                  const text = genLabel(keyLevel, price);

                  const createLabel =
                    !labelCreated &&
                    ((!priceScale && dateIdx === 0) ||
                      (priceScale &&
                        priceScale.from <= price &&
                        price <= priceScale.to));
                  if (createLabel) {
                    labelCreated = true;
                  }

                  return [
                    chart
                      .createMultipointShape(
                        [
                          { time: beg, price },
                          { time: end, price },
                        ],
                        {
                          shape: 'trend_line',
                          showPrice: false,
                          lock: true,
                          disableSave: true,
                          disableUndo: true,
                          overrides: {
                            linecolor: this.datafeed.theme?.palette.hiro
                              .priceLines[
                              REVERSE_FIELD_NAME_MAPPING[keyLevel]
                            ] as string,
                          },
                        },
                      )
                      .then((id) => {
                        entityIds.push(id);
                      }),
                    // only draw a label if this is the first level in the visible range
                    createLabel &&
                      chart
                        .createShape(
                          {
                            time: beg,
                            price: price - priceFraction * numPriorLabels,
                          },
                          {
                            shape: 'text',
                            showPrice: false,
                            lock: true,
                            //disableSelection: true,
                            disableSave: true,
                            disableUndo: true,
                            text,
                            zOrder: 'top',
                            overrides: {
                              showPrice: true,
                              color: this.datafeed.theme?.palette.hiro
                                .priceLines[
                                REVERSE_FIELD_NAME_MAPPING[keyLevel]
                              ] as string,
                            },
                          },
                        )
                        .then((id) => {
                          entityIds.push(id);
                        }),
                  ];
                },
              );
            },
          ),
      ),
    );
  }
}

const genLabel = (k: PrikeLineKey, price: number): string => {
  const formattedPrice = price.toLocaleString(undefined, {
    maximumFractionDigits: 2,
  });
  return `${k} $${formattedPrice}`;
};
